import { Flex } from 'antd';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_MODAL_WIDTH,
  Heading,
  IconButton,
  Modal,
  Paragraph,
  Tooltip,
  type ModalProps,
} from '../Ant';
import { Close } from '../icons';

type ClosableModalProps = Omit<
  ModalProps,
  'title' | 'onCancel' | 'closable' | 'closeIcon' | 'footer' | 'aria-labelledby'
> & {
  title: string;
  subtitle?: string;
  onClose: () => void;
  'data-testid'?: string;
};

/** A dialog closed from its titled header; confirmation dialogs without a close button don't use it. */
const ClosableModal = ({
  title,
  subtitle,
  onClose,
  children,
  width = DEFAULT_MODAL_WIDTH,
  'data-testid': testId,
  ...props
}: ClosableModalProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const closeLabel = t('common:bottomNavigation.close');

  return (
    <Modal
      centered
      {...props}
      width={width}
      title={null}
      closeIcon={null}
      footer={null}
      aria-labelledby={titleId}
      onCancel={onClose}
      data-testid={testId}
    >
      <Flex vertical gap={5} className="closable-modal-header p-15">
        <Flex justify="space-between" align="center" gap={10}>
          <Heading
            id={titleId}
            level={3}
            $fontsize="16px"
            $weight={600}
            $color="title"
            $lineheight="20px"
          >
            {title}
          </Heading>
          <Tooltip title={closeLabel} arrow={false} placement="top">
            <IconButton
              className="closable-modal-close"
              aria-label={closeLabel}
              onClick={onClose}
              icon={<Close className="fz-16" />}
              data-testid={testId && `${testId}-close`}
            />
          </Tooltip>
        </Flex>
        {subtitle && (
          <Paragraph $fontsize="13px" $lineheight="20px" $color="description">
            {subtitle}
          </Paragraph>
        )}
      </Flex>
      {children}
    </Modal>
  );
};

export default ClosableModal;
